import React, {
  useEffect,
  useRef
} from "react";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";
import _ from "lodash";
import { toast } from "react-toastify";
import moment from "moment-timezone";

import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text";
import { useSearchApi } from "src/core/shared/api/search.api";
import { ProductSearchComponent } from "src/core/products/components/ProductSearchComponent";
import {
  IProduct,
  ISearchRequest
} from "src/core/products/api/products.api";
import { Button } from "src/library/components/Button";
import DraggableDatedProducts from "src/core/products/components/DraggableDatedProducts";
import { DraggableDatedProduct } from "src/core/products/components/DraggableDatedProductsRow";
import generateListProductsParams from "src/core/products/utils/generate-list-products-params";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

import { CreateUpdateFeaturedProduct } from "../../../modules/promotions/api/featured-products.api";
import useFeaturedProducts from "../../../modules/promotions/hooks/useFeaturedProducts";

/**
 * Featured Products Page used for managing featured products as admin
 */
const FeaturedProductsPage: React.ReactFCWithChildren<PageProps<unknown>> = ({ title, ...props }) => {
  const [ listSearchResponse, getSearch ] = useSearchApi("SEARCH_PRODUCTS");
  const draggableDatedProductsListRef = useRef<{currentDatedProducts: DraggableDatedProduct[]}>(null);
  const checkPermission = useCheckUserPermission();
  const canAmendPromo = checkPermission("amend-promotion");

  const {
    isLoading, featuredProducts, setCurrentFeaturedProducts, bulkUpdateFeaturedProductsReq, getFeaturedProductsReq
  } = useFeaturedProducts();

  // on mount, get latest featured products
  useEffect(() => {
    getFeaturedProductsReq(null);
  }, [ getFeaturedProductsReq ]);

  // handle selecting a product
  const productDownShiftProps = useCombobox<IProduct>({
    items: listSearchResponse.data ? listSearchResponse.data.result : [],
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        if (featuredProducts.length) {
          // avoid duplicate products in list
          const productAlreadyExists = _.find(featuredProducts, [ "productId", changes.selectedItem.id ]);

          if (productAlreadyExists) {
            toast.error(`The product ${changes.selectedItem.partNumber} is already part of featured products.`);

            return;
          }
        }

        const newFeaturedProduct: CreateUpdateFeaturedProduct = {
          productId: changes.selectedItem.id,
          startDate: moment.utc().toDate(),
          priority: 1,
          product: changes.selectedItem
        };

        // when adding new featured product, shift priority of all other featured products by 1
        setCurrentFeaturedProducts([
          newFeaturedProduct,
          ..._.map(featuredProducts, featuredProduct => ({
            ...featuredProduct,
            priority: featuredProduct.priority + 1
          }))
        ]);

        return;
      }
    }
  });

  return (
    <Layout
      title={title}
      {...props}
    >
      <MainContentWrapper fill>
        <FeaturedProductsContainer>
          <Text as="h4">
            Featured Products
          </Text>

          <Text>
            To add add featured products, search below and select the product.
          </Text>

          <div className="product-search">
            <ProductSearchComponent
              error={listSearchResponse.error}
              onSearch={(data: ISearchRequest) => getSearch(generateListProductsParams(data))}
              loading={listSearchResponse.pending}
              searchResults={listSearchResponse.data ? listSearchResponse.data.result : []}
              downshiftProps={productDownShiftProps}
              placeholder={`Search ${props.terminology.product.plural.text}...`}
              idsToDisable={_.compact(_.map(featuredProducts, "productId"))}
            />
          </div>

          {featuredProducts.length > 0 && (
            <Text>
              Drag and drop to change the priority order of featured products.
            </Text>
          )}

          <div className={`${featuredProducts.length > 0 ? "mt" : ""}`}>
            <DraggableDatedProducts
              datedProducts={featuredProducts as DraggableDatedProduct[] || []}
              ref={draggableDatedProductsListRef}
              isLoading={isLoading}
              disableInputs={!canAmendPromo}
              lockSortableFeature={!canAmendPromo}
            />
          </div>

          <div className="save">
            <Button
              $buttonType="primary"
              onClick={() => {
                const currentFeaturedProducts = draggableDatedProductsListRef?.current?.currentDatedProducts;

                if (!canAmendPromo) {
                  return toast.error("You do not have permission to edit featured products. Please speak to your administrator to request permission.");
                }

                if (currentFeaturedProducts) {
                  return bulkUpdateFeaturedProductsReq({
                    data: currentFeaturedProducts.map(featuredProduct => ({
                      ...featuredProduct,
                      priority: featuredProduct.priority || 0,
                      startDate: moment.utc(featuredProduct.startDate).format("YYYY-MM-DD"),
                      endDate: featuredProduct.endDate ? moment.utc(featuredProduct.endDate).format("YYYY-MM-DD") : undefined
                    }))
                  });
                }

                return toast.error("Error saving featured products");
              }}
              loading={isLoading}
            >
              Save
            </Button>
          </div>
        </FeaturedProductsContainer>
      </MainContentWrapper>
    </Layout>
  );
};

const FeaturedProductsContainer = styled(Grid.Bounds)`
  ${({ theme }) => css`
    flex-direction: column;
    padding: ${theme.spacing.padding.xs};

    .mt {
      margin-top: ${theme.spacing.margins.lg};
    }

    .product-search {
      margin-top: ${theme.spacing.margins.lg};
      margin-bottom: ${theme.spacing.margins.lg};
    }

    .save {
      padding-top: ${theme.spacing.padding.lg};
    }
  `}
`;

export default FeaturedProductsPage;
